import { CLASS_GROUPS } from "#constants/classOptions"
import {
  DISCOUNT_TYPES,
  getSuppliesFeeById,
  getSuppliesFeeIdForCoreSkillLevel,
  hasMaterialsFee,
  hasSuppliesFee,
  isPortfolioTuition,
  SCULPTURE_MATERIALS_FEE_PER_SESSION,
  shouldUseSemesterRate,
  SUPPLIES_FEES,
  type SuppliesFeeId,
  TUITION_RATES,
} from "#constants/feeConstants"
import { type CoreSkillLevel } from "#constants/skillLevelConstants"

export type FeeBreakdown = {
  tuitionPerSession: number
  tuitionTotal: number
  tuitionAfterDiscount: number
  suppliesFee: number
  materialsFee: number
  discountAmount: number
  discountType: DISCOUNT_TYPES | null
  grandTotal: number
  sessionCount: number
  usingSemesterRate: boolean
}

/**
 * Calculates the tuition rate per session based on class group and session count
 */
export const getTuitionRatePerSession = (
  classGroup: CLASS_GROUPS,
  sessionCount: number,
  totalSessionsRemaining: number
): { rate: number; usingSemesterRate: boolean } => {
  const isPortfolio = isPortfolioTuition(classGroup)
  const rates = isPortfolio ? TUITION_RATES.portfolio : TUITION_RATES.standard
  const usingSemesterRate = shouldUseSemesterRate(
    sessionCount,
    totalSessionsRemaining
  )

  return {
    rate: usingSemesterRate ? rates.semester : rates.monthly,
    usingSemesterRate,
  }
}

/**
 * Calculates tuition total for a given number of sessions
 */
export const calculateTuition = (
  classGroup: CLASS_GROUPS,
  sessionCount: number,
  totalSessionsRemaining: number
): { total: number; perSession: number; usingSemesterRate: boolean } => {
  const { rate, usingSemesterRate } = getTuitionRatePerSession(
    classGroup,
    sessionCount,
    totalSessionsRemaining
  )

  return {
    total: rate * sessionCount,
    perSession: rate,
    usingSemesterRate,
  }
}

/**
 * Applies a 10% discount and rounds UP the final price
 * (Meaning the discount is effectively rounded down)
 */
export const applyDiscount = (
  amount: number,
  discountType: DISCOUNT_TYPES | null
): { discountedAmount: number; discountValue: number } => {
  if (!discountType) {
    return { discountedAmount: amount, discountValue: 0 }
  }

  // 10% discount, round up final price
  const discountedAmount = Math.ceil(amount * 0.9)
  const discountValue = amount - discountedAmount

  return { discountedAmount, discountValue }
}

/**
 * Gets the default supplies fee for a class group and skill level
 * Returns null if the class group doesn't have supplies fees
 */
export const getDefaultSuppliesFee = (
  classGroup: CLASS_GROUPS,
  skillLevel: string | null | undefined
): { feeId: SuppliesFeeId | null; price: number } => {
  if (!hasSuppliesFee(classGroup)) {
    return { feeId: null, price: 0 }
  }

  // Portfolio has its own supplies fee
  if (classGroup === CLASS_GROUPS.PORTFOLIO) {
    return {
      feeId: "collegePortfolioSupplies",
      price: SUPPLIES_FEES.collegePortfolioSupplies.price,
    }
  }

  // Core and Painting use skill-level based supplies
  if (
    classGroup === CLASS_GROUPS.CORE ||
    classGroup === CLASS_GROUPS.PAINTING
  ) {
    if (!skillLevel) {
      // Default to basic supplies if no skill level
      return {
        feeId: "coreArtBasicSupplies",
        price: SUPPLIES_FEES.coreArtBasicSupplies.price,
      }
    }

    const feeId = getSuppliesFeeIdForCoreSkillLevel(
      skillLevel as CoreSkillLevel
    )
    if (feeId) {
      return { feeId, price: getSuppliesFeeById(feeId).price }
    }
  }

  return { feeId: null, price: 0 }
}

/**
 * Calculates materials fee (currently only for Sculpture)
 */
export const calculateMaterialsFee = (
  classGroup: CLASS_GROUPS,
  sessionCount: number
): number => {
  if (!hasMaterialsFee(classGroup)) {
    return 0
  }

  return SCULPTURE_MATERIALS_FEE_PER_SESSION * sessionCount
}

/**
 * Calculates the complete fee breakdown
 */
export const calculateFeeBreakdown = (
  classGroup: CLASS_GROUPS,
  sessionCount: number,
  totalSessionsRemaining: number,
  skillLevel: string | null | undefined,
  discountType: DISCOUNT_TYPES | null,
  waiveSupplies: boolean = false
): FeeBreakdown => {
  // Calculate tuition
  const {
    total: tuitionTotal,
    perSession,
    usingSemesterRate,
  } = calculateTuition(classGroup, sessionCount, totalSessionsRemaining)

  // Apply discount to tuition only
  const { discountedAmount: tuitionAfterDiscount, discountValue } =
    applyDiscount(tuitionTotal, discountType)

  // Get supplies fee (waivable)
  const { price: suppliesFee } = waiveSupplies
    ? { price: 0 }
    : getDefaultSuppliesFee(classGroup, skillLevel)

  // Calculate materials fee (per session, not waivable)
  const materialsFee = calculateMaterialsFee(classGroup, sessionCount)

  // Grand total
  const grandTotal = tuitionAfterDiscount + suppliesFee + materialsFee

  return {
    tuitionPerSession: perSession,
    tuitionTotal,
    tuitionAfterDiscount,
    suppliesFee,
    materialsFee,
    discountAmount: discountValue,
    discountType,
    grandTotal,
    sessionCount,
    usingSemesterRate,
  }
}

/**
 * Formats a fee breakdown into a display-friendly object
 */
export const formatFeeBreakdown = (
  breakdown: FeeBreakdown
): {
  tuitionLine: string
  suppliesLine: string | null
  materialsLine: string | null
  discountLine: string | null
  totalLine: string
} => {
  const rateType = breakdown.usingSemesterRate ? "Semester" : "Monthly"

  return {
    tuitionLine: `${rateType} Rate ($${breakdown.tuitionPerSession} x ${breakdown.sessionCount}) = $${breakdown.tuitionAfterDiscount}`,
    suppliesLine:
      breakdown.suppliesFee > 0 ? `Supplies: $${breakdown.suppliesFee}` : null,
    materialsLine:
      breakdown.materialsFee > 0
        ? `Materials ($${SCULPTURE_MATERIALS_FEE_PER_SESSION} x ${breakdown.sessionCount}): $${breakdown.materialsFee}`
        : null,
    discountLine:
      breakdown.discountAmount > 0
        ? `${breakdown.discountType === DISCOUNT_TYPES.SIBLING ? "Sibling" : "Extra Class"} Discount: -$${breakdown.discountAmount}`
        : null,
    totalLine: `Total: $${breakdown.grandTotal}`,
  }
}
